import { useState } from "react";

import { messages } from "../../lib/messages";
import { formatPhoneInput } from "../../lib/phone";
import { hapticFeedback, isRequestContactAvailable, requestContact } from "../../lib/telegram";

const PHONE_PATTERN = String.raw`^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$`;

interface PhoneInputProps {
  value: string;
  onChange(value: string): void;
  error?: string;
}

/**
 * PhoneInput — маскированное поле телефона чекаута с кнопкой «Взять из
 * Telegram» (docs/07-miniapp-ux.md §2.5). Кнопка скрыта, если клиент
 * Telegram не поддерживает requestContact — вычисляется один раз при
 * монтировании, доступность не меняется на лету.
 *
 * Отказ покупателя и техническая неудача разведены: отказ — его решение
 * и молчит, неудача показывает подсказку, иначе кнопка выглядит сломанной.
 */
export function PhoneInput({ value, onChange, error }: PhoneInputProps) {
  const [contactAvailable] = useState(isRequestContactAvailable);
  const [requestFailed, setRequestFailed] = useState(false);

  async function handleTakeFromTelegram() {
    const result = await requestContact();
    if (result.status === "declined") {
      return;
    }
    if (result.status === "failed") {
      setRequestFailed(true);
      return;
    }
    setRequestFailed(false);
    onChange(formatPhoneInput(result.phone));
    hapticFeedback.impactOccurred("light");
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor="checkout-phone" className="text-sm text-hint">
        {messages.phoneLabel}
      </label>
      <div className="flex gap-2">
        <input
          id="checkout-phone"
          name="contact_phone"
          type="tel"
          inputMode="tel"
          required
          pattern={PHONE_PATTERN}
          title="Введите номер полностью: +7 (999) 123-45-67"
          value={value}
          onChange={(event) => onChange(formatPhoneInput(event.target.value))}
          placeholder="+7 (___) ___-__-__"
          className="flex-1 rounded-card bg-surface px-4 py-2 text-text placeholder:text-hint"
        />
        {contactAvailable ? (
          <button
            type="button"
            onClick={() => void handleTakeFromTelegram()}
            className="shrink-0 rounded-card bg-surface px-3 text-sm text-link"
          >
            {messages.takeFromTelegram}
          </button>
        ) : null}
      </div>
      {error ? <p className="text-sm text-red-500">{error}</p> : null}
      {requestFailed ? <p className="text-sm text-hint">{messages.takeFromTelegramFailed}</p> : null}
    </div>
  );
}
